import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { UpdateConfigRequest } from '@weave/schema-gen-ts/dist/schemas/phone/devices/config/device_config.pb';
import { Device, DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import {
  RenameLineChangeSet,
  RenameLineResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone-devices/lines/line_api.pb';
import { useQueryClient } from 'react-query';
import { DevicesApi, DevicesQueries, DevicesQueryKey, DevicesTypes } from '@frontend/api-devices';
import { E911Api, E911QueryKeys } from '@frontend/api-e911-addresses';
import { LocationsApi } from '@frontend/api-locations';
import { SipProfilesApiTypes } from '@frontend/api-sip-profile';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation, useQuery } from '@frontend/react-query-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useTimezone } from '@frontend/shared';
import {
  ContentLoader,
  Heading,
  useModalControl,
  useAlert,
  DropdownField,
  useFormField,
  Modal,
  Button,
  Text,
  useTooltip,
} from '@frontend/design-system';
import { EditableField } from '../../../components/common/editable-cell';
import { queryKeys } from '../../../query-keys';
import { formatE911AddressForDisplay, isInvalidE911AddressError } from '../../../utils/e911-address';
import { logError } from '../../../utils/loggers';
import { validateDeviceName, validateExtension } from '../../../utils/validators';
import {
  buildRenameLineSuccessMessage,
  getChangedFields,
  updateDeviceCaches,
} from '../../../views/settings/devices/rename-line-helpers';
import { E911AddressModal } from '../e911/e911-address-modal';
import { E911Select } from '../e911/e911-select';
import { getE911CreateSuccessMessage, useCreateE911Address } from '../e911/use-e911-address-form';
import { MediaConfigModal } from './media-config.modal';

type DeviceInfoProps = {
  sipProfile: SipProfilesApiTypes.SipProfileType | undefined;
  locationIds?: string[];
  device?: Device;
  tenantId?: string;
};

type TimezoneOption = {
  region: string;
  city: string;
  state?: string;
  province?: string;
  country: string;
};

const getDisplayCountry = (country?: string) => {
  if (country === 'United States') return 'US';
  if (country === 'Canada') return 'CA';
  return country;
};

const formatTimezoneLabel = (timezone: TimezoneOption) => {
  return `${timezone.region} Time (${timezone.city}, ${timezone.state ?? timezone.province}, ${getDisplayCountry(
    timezone.country
  )})`;
};

export const DeviceInfo = ({ sipProfile, device, tenantId, locationIds }: DeviceInfoProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const alerts = useAlert();
  const deviceId = device?.deviceId ?? '';
  const addressModalControl = useModalControl();
  const queryClient = useQueryClient();
  const { name, extension, internalCallerName } = sipProfile ?? {};
  const { modalProps, openModal, closeModal } = useModalControl();
  const { useGetDeviceE911Address } = DevicesQueries;
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasFeatureFlagCustomBackground = getFeatureFlagValue('device-custom-background');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const locationId = settingsTenantLocation?.locationId;
  const { data: officeTimezone } = useQuery({
    queryKey: [locationId, ...queryKeys.timezone(locationId || '')],
    queryFn: () => LocationsApi.getOfficeTimezone({}, { locationId: locationId }),
    select: (data) => data.timezone,
    enabled: !!locationId && locationId !== 'null',
    meta: {
      error: {
        message: 'DeviceInfo:useQuery | Failed to fetch office timezone',
        data: { locationId },
      },
    },
  });

  const { data: { e911Address } = { e911Address: undefined }, isLoading: isLoadingE911Address } =
    useGetDeviceE911Address(
      {
        deviceId,
      },
      { enabled: !!deviceId }
    );

  const [addressId, setAddressId] = useState<string | undefined>(e911Address?.id);

  const { data: { addresses } = { addresses: [] }, isRefetching: isLoadingAddresses } = useQuery({
    queryKey: [tenantId, ...E911QueryKeys.queryKeys.listAddresses()],
    queryFn: () => E911Api.list({ tenantId: tenantId ?? '' }),
    enabled: !!tenantId,
    select: (data) => ({
      ...data,
      addresses: data.addresses
        ?.slice()
        .sort((firstAddress, secondAddress) => firstAddress.name.localeCompare(secondAddress.name)),
    }),
    meta: {
      error: {
        message: 'DeviceInfo:useQuery | Failed to fetch E911 addresses',
        data: { tenantId },
      },
    },
  });

  const { data: extData } = DevicesQueries.useListExtensions(
    { tenantId: tenantId ?? '' },
    { enabled: tenantId !== undefined }
  );
  const extensionsInUse = new Set(extData?.extensions?.map((ext) => ext.number) ?? []);
  const currentExtension = sipProfile?.extension?.extensionNumber;
  if (currentExtension) {
    // Remove the extension that is currently in use by this device from the set of extensions in use to allow the
    // user to keep their current extension number if they choose to.
    extensionsInUse.delete(currentExtension);
  }

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  useEffect(() => {
    setAddressId(e911Address?.id);
  }, [e911Address?.id]);

  const { mutateAsync: mutateDevice } = useMutation({
    mutationFn: ({ device }: DevicesTypes.UpdateDeviceType['input']) =>
      DevicesApi.UpdateDevice({
        device,
      }),
  });

  // Highlights the changed fields briefly after a successful save.
  // Adds a CSS class that snaps on the highlight, then removes it to let the CSS transition fade it out.
  const flashChanges = useCallback((changes: RenameLineChangeSet | undefined) => {
    if (!changes) return;
    const fieldIds: string[] = [];
    if (changes.extensionNumber && changes.extensionNumber.before !== changes.extensionNumber.after) {
      fieldIds.push('extensionNumber');
    }
    if (changes.deviceName && changes.deviceName.before !== changes.deviceName.after) {
      fieldIds.push('deviceName');
    }
    if (changes.internalCallerName && changes.internalCallerName.before !== changes.internalCallerName.after) {
      fieldIds.push('internalCallerName');
    }
    if (fieldIds.length === 0) return;

    fieldIds.forEach((fieldId) => {
      const el = document.querySelector(`.device-info-field-${fieldId}`);
      el?.classList.add('highlighted');
      setTimeout(() => el?.classList.remove('highlighted'), 800);
    });
  }, []);

  const handleSaveSuccess = useCallback(
    (targetDeviceId: string, response: RenameLineResponse) => {
      updateDeviceCaches({
        queryClient,
        changes: response.changes,
        deviceId: targetDeviceId,
        tenantId,
        sipProfileId: response.line?.sipProfile.id,
      });

      const changedFields = getChangedFields(response.changes);
      const message = buildRenameLineSuccessMessage(changedFields);
      // invalidate extensions list to update the "in use" status of extensions after a change to an extension number
      queryClient.invalidateQueries([tenantId, ...DevicesQueryKey.queryKeys.listExtensions()]);
      alerts.success(message);

      flashChanges(response.changes);
    },
    [queryClient, tenantId, alerts, flashChanges]
  );

  const { mutate: renameLine, isLoading: isRenamingLine } = useMutation({
    mutationFn: DevicesApi.RenameLine,
    onSuccess: (response) => {
      handleSaveSuccess(deviceId, response);
    },
    onError: (error) => {
      alerts.error(t('Failed to save changes'));
      logError('DeviceInfo:renameLineMutation | Failed to rename line', {
        data: { deviceId, tenantId, error },
      });
    },
  });

  const handleUpdateAddressError = (error: unknown) => {
    if (isInvalidE911AddressError(error)) {
      alerts.error(t('Unable to verify address with your E911 provider'));
    } else {
      alerts.error(t('Failed to update E911 address'));
    }
    logError('DeviceInfo:updateAddressMutation | Failed to update E911 address', {
      data: { deviceId, tenantId, error },
    });
  };

  const { mutateAsync: updateAddress, isLoading: isUpdatingAddress } = useMutation({
    mutationFn: (id: string) =>
      DevicesApi.UpdateDeviceE911Address({
        deviceId,
        e911Address: {
          id,
          name: '',
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([tenantId, ...DevicesQueryKey.queryKeys.devicesList()]);
      queryClient.invalidateQueries([deviceId, ...DevicesQueryKey.queryKeys.deviceAddressById()]);
      queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);

      alerts.success(t('Updated E911 Address'));
    },
    onError: handleUpdateAddressError,
  });

  const {
    createAddress,
    isCreatingAddress: isLoadingCreateAddress,
    addressSaveError,
    clearAddressSaveError,
  } = useCreateE911Address({
    tenantId,
    onSuccess: (data, submittedAddress) => {
      if (addresses?.some(({ id }) => id === data.id)) {
        alerts.warning(
          t('Duplicate address. {{addressName}} ({{address}})', {
            addressName: data.name || t('Address'),
            address: formatE911AddressForDisplay(data),
          })
        );
      } else {
        alerts.success(getE911CreateSuccessMessage(t, submittedAddress, data));
      }
      updateAddress(data.id);
    },
  });

  const isLoading = isRenamingLine;

  const handleE911OnChange = (id: string) => {
    if (id === 'addNewAddress') {
      addressModalControl.openModal();
      setAddressId('addNewAddress');
    } else {
      updateAddress(id);
    }
  };

  const { timezonesByCountry } = useTimezone();
  const fieldProps = useFormField({ type: 'dropdown', value: device?.timeZone ?? '' }, [device?.timeZone]);
  const isSelectedOfficeDefaultTimezoneInField = fieldProps.value === officeTimezone;
  const {
    Tooltip: HoverLabel,
    triggerProps: defaultTimezoneTriggerProps,
    tooltipProps: defaultTimezoneTooltipProps,
  } = useTooltip({ placement: 'top' });
  const {
    Tooltip: ClosedDropdownHoverLabel,
    triggerProps: closedDropdownTriggerProps,
    tooltipProps: closedDropdownTooltipProps,
  } = useTooltip({ placement: 'top' });

  const sortedTimezoneGroups = useMemo(
    () =>
      Object.entries(timezonesByCountry)
        .map(([key, timezoneList]) => {
          const sortedTimezones = [...timezoneList].sort((a, b) => {
            if (a.name === officeTimezone) return -1;
            if (b.name === officeTimezone) return 1;

            const aLabel = formatTimezoneLabel(a);
            const bLabel = formatTimezoneLabel(b);
            return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
          });

          const hasOfficeDefaultTimezone = sortedTimezones.some((tx) => tx.name === officeTimezone);

          return {
            key,
            timezones: sortedTimezones,
            hasOfficeDefaultTimezone,
          };
        })
        .sort((a, b) => {
          if (a.hasOfficeDefaultTimezone) return -1;
          if (b.hasOfficeDefaultTimezone) return 1;

          return a.key.localeCompare(b.key, undefined, { sensitivity: 'base' });
        }),
    [timezonesByCountry, officeTimezone]
  );

  const { data: { config } = { config: {} } } = useQuery({
    queryKey: [deviceId, 'devices-primitive-type-config'],
    queryFn: () =>
      DevicesApi.GetPrimitiveTypeConfig({
        primitiveType: 'device',
        primitiveId: deviceId,
      }),
    meta: {
      error: {
        message: 'DeviceInfo:useQuery | Failed to fetch device primitive config',
        data: { deviceId },
      },
    },
    enabled: !!deviceId,
  });

  const { mutate: uploadConfig } = useMutation({
    mutationFn: (payload: UpdateConfigRequest) =>
      DevicesApi.UploadMedia({
        ...payload,
        config: payload.config ?? {},
      }),
    onSuccess: (_, request) => {
      queryClient.invalidateQueries([deviceId, 'devices-primitive-type-config']);
      closeModal();
      if (!!request.config?.wallpaperURL) {
        alerts.success(t('Successfully uploaded background image to {{ name }}', { name: name ?? 'device' }));
      } else {
        alerts.success(t('Successfully removed background image from {{ name }}', { name: name ?? 'device' }));
      }
    },
    onError: (error) => {
      const errorMessage = t('Failed to upload device background image');
      alerts.error(errorMessage);
      logError(error instanceof Error ? error : errorMessage, {
        name: 'DeviceInfo:uploadConfig',
        data: {
          deviceId,
          tenantId,
        },
      });
    },
  });

  const hasExtension = Boolean(extension?.extensionId || extension?.extensionNumber);

  return (
    <>
      <ContentLoader show={isLoading} />
      {hasExtension && (
        <div className='device-info-field-extensionNumber' css={[columnSpacing, fieldHighlightStyle]}>
          <Heading level={3}>{t('Device Extension Number')}</Heading>
          <EditableField
            value={extension?.extensionNumber?.toString() ?? ''}
            displayName={t('Extension Number')}
            fieldId='extensionNumber'
            isEditing={editingFieldId === 'extensionNumber'}
            isSaving={isRenamingLine}
            onStartEdit={setEditingFieldId}
            onCancelEdit={() => setEditingFieldId(null)}
            validatorFn={(value) => validateExtension(value, extensionsInUse)}
            helperText={t('Choose an extension number that is not currently in use.')}
            trackingIdPrefix='pc-device-settings-details-extension-number-'
            alwaysShowEditIcon
            wrapperCss={editableFieldWidth}
            onSave={(newValue) => {
              renameLine({
                tenantId: tenantId ?? '',
                extensionId: extension?.extensionId ?? '',
                deviceId,
                extensionNumber: newValue,
              });
            }}
          />
        </div>
      )}
      <div className='device-info-field-deviceName' css={[columnSpacing, fieldHighlightStyle]}>
        <div>
          <Heading level={3}>{t('Device Name')}</Heading>
          <Text size='small' color='subdued'>
            {t(
              "Identifies this device in your settings. It appears on this phone's screen and also in the contact directories and line keys of other desk phones."
            )}
          </Text>
        </div>
        <EditableField
          value={name ?? ''}
          displayName={t('Device Name')}
          fieldId='deviceName'
          isEditing={editingFieldId === 'deviceName'}
          isSaving={isRenamingLine}
          onStartEdit={setEditingFieldId}
          onCancelEdit={() => setEditingFieldId(null)}
          validatorFn={validateDeviceName}
          trackingIdPrefix='pc-device-settings-details-device-name-'
          alwaysShowEditIcon
          wrapperCss={editableFieldWidth}
          onSave={(newValue) => {
            renameLine({
              tenantId: tenantId ?? '',
              extensionId: extension?.extensionId ?? '',
              deviceId,
              deviceName: newValue,
            });
          }}
        />
      </div>
      <div className='device-info-field-internalCallerName' css={[columnSpacing, fieldHighlightStyle]}>
        <div>
          <Heading level={3}>{t('Internal Caller ID Name')}</Heading>
          <Text size='small' color='subdued'>
            {t('This is the name displayed when calling another extension within your office.')}
          </Text>
        </div>
        <EditableField
          value={internalCallerName ?? ''}
          displayName={t('Internal Caller ID Name')}
          fieldId='internalCallerName'
          isEditing={editingFieldId === 'internalCallerName'}
          isSaving={isRenamingLine}
          onStartEdit={setEditingFieldId}
          onCancelEdit={() => setEditingFieldId(null)}
          validatorFn={validateDeviceName}
          trackingIdPrefix='pc-device-settings-details-internal-caller-name-'
          alwaysShowEditIcon
          wrapperCss={editableFieldWidth}
          onSave={(newValue) => {
            renameLine({
              tenantId: tenantId ?? '',
              extensionId: extension?.extensionId ?? '',
              deviceId,
              internalCallerName: newValue,
              sipProfileId: sipProfile?.sipProfileId ?? '',
            });
          }}
        />
      </div>
      <div css={columnSpacing}>
        <Heading level={3}>{t('E911 Address')}</Heading>
        <E911Select
          addAnAddress
          addresses={addresses}
          addressId={addressId ?? ''}
          onChange={handleE911OnChange}
          label={t('Select an E911 Address')}
          helperText={t('This should be the physical location where this device is used.')}
          isLoadingE911Address={isLoadingE911Address || isLoadingAddresses || isUpdatingAddress}
        />
      </div>
      {device?.type === DeviceType_Enum.DESK_PHONE && (
        <>
          <div css={columnSpacing}>
            <Heading level={3}>{t('Device Time Zone')}</Heading>
            <div
              css={{
                width: 'fit-content',
              }}
              {...(isSelectedOfficeDefaultTimezoneInField ? closedDropdownTriggerProps : {})}
            >
              <DropdownField
                {...fieldProps}
                css={css`
                  width: fit-content;
                  min-width: 416px;
                  max-width: 416px;
                `}
                label={t('Select a Time Zone')}
                name='device-timezone'
                helperText={t('This will set the time zone for the clock and the call history on the device.')}
                startAdornment={
                  isSelectedOfficeDefaultTimezoneInField ? (
                    <div css={{ display: 'inline-flex', marginRight: 'var(--fabric-space-gap-xs)' }}>
                      <Icon name='building-small' color='light' />
                    </div>
                  ) : undefined
                }
                onChange={({ name, value }) => {
                  fieldProps.onChange({ name, value });
                  mutateDevice({
                    device: {
                      ...device,
                      timeZone: value,
                    },
                  })
                    .then(() => {
                      queryClient.invalidateQueries([deviceId, ...DevicesQueryKey.queryKeys.deviceById()]);
                      alerts.success(t('Updated time zone.'));
                    })
                    .catch((error) => {
                      alerts.error(t('Failed to update time zone'));
                      logError('DeviceInfo:mutateDevice | Failed to update device time zone', {
                        data: { deviceId, tenantId, error },
                      });
                    });
                }}
              >
                {sortedTimezoneGroups.map(({ key, timezones }) => (
                  <DropdownField.OptionGroup key={key} label={key}>
                    {timezones.map((timezone) => {
                      const isOfficeDefaultTimezone = officeTimezone === timezone.name;
                      const timezoneLabel = formatTimezoneLabel(timezone);
                      return (
                        <DropdownField.Option
                          key={timezone.name}
                          value={timezone.name}
                          searchValue={timezone.name}
                          displayValue={timezoneLabel}
                        >
                          <div
                            css={timezoneOptionContent}
                            {...(isOfficeDefaultTimezone ? defaultTimezoneTriggerProps : {})}
                          >
                            {isOfficeDefaultTimezone && fieldProps.active && (
                              <>
                                <Icon
                                  name='building-small'
                                  color='light'
                                  css={{ position: 'absolute', left: 'var(--fabric-space-gap-xs)' }}
                                />
                                <HoverLabel {...defaultTimezoneTooltipProps}>{t('Default time zone')}</HoverLabel>
                              </>
                            )}
                            <div>{timezoneLabel}</div>
                          </div>
                        </DropdownField.Option>
                      );
                    })}
                  </DropdownField.OptionGroup>
                ))}
              </DropdownField>
              {isSelectedOfficeDefaultTimezoneInField && (
                <ClosedDropdownHoverLabel {...closedDropdownTooltipProps}>
                  {t('Default time zone')}
                </ClosedDropdownHoverLabel>
              )}
            </div>
          </div>
          {hasFeatureFlagCustomBackground && (
            <div css={columnSpacing}>
              <Heading level={3}>{t('Background Image')}</Heading>
              {Object.keys(config ?? {}).length > 0 ? (
                <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
                  <img
                    loading='lazy'
                    src={config?.wallpaperURL}
                    alt={t('Device Background Image')}
                    css={{
                      borderRadius: 6,
                      width: '300px',
                      height: '300px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                  <Button
                    onClick={() =>
                      uploadConfig({
                        config: {
                          ...config,
                          wallpaperURL: undefined,
                        },
                        primitiveType: 'device',
                        primitiveId: deviceId,
                      })
                    }
                    variant='secondary'
                    destructive
                  >
                    <Icon name='trash' />
                  </Button>
                  <Button variant='secondary' onClick={openModal}>
                    <Icon name='edit' />
                  </Button>
                </div>
              ) : (
                <Button css={{ maxWidth: '250px' }} variant='secondary' onClick={openModal}>
                  {t('Add Background Image')}
                </Button>
              )}
            </div>
          )}
        </>
      )}
      <Modal {...modalProps} minWidth={700} maxWidth={700}>
        <MediaConfigModal
          uploadConfig={uploadConfig}
          closeModal={closeModal}
          deviceId={deviceId}
          locationIds={locationIds ?? []}
          currentConfig={config}
        />
      </Modal>
      <E911AddressModal
        isLoading={isLoadingCreateAddress}
        {...addressModalControl.modalProps}
        addressSaveError={addressSaveError}
        clearAddressSaveError={clearAddressSaveError}
        onCancel={() => {
          setAddressId(e911Address?.id);
          addressModalControl.modalProps.onClose();
        }}
        onSave={createAddress}
        addresses={addresses}
        onUseExistingAddress={(address) => {
          setAddressId(address.id);
          addressModalControl.closeModal();
          updateAddress(address.id);
        }}
      />
    </>
  );
};

const columnSpacing = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  margin-bottom: var(--fabric-space-gap-md);
`;

const editableFieldWidth = css`
  width: 400px;
  min-width: 400px;
`;

const timezoneOptionContent = css`
  align-items: center;
  display: inline-flex;
  gap: var(--fabric-space-gap-xs);
`;

const fieldHighlightStyle = css`
  border-radius: var(--fabric-border-radius-small);
  outline: 4px solid transparent;
  transition: background 0.8s ease-in-out, outline-color 0.8s ease-in-out;

  &.highlighted {
    background: var(--fabric-color-indigo-10);
    outline-color: var(--fabric-color-indigo-10);
    transition: none;
  }
`;
